import React, { useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import RootState from "../../../redux/states/root.state";
import AutoRefreshControl from "../../../components/common/AutoRefresh/AutoRefreshControl";
import MigrationProgressTable from "./MigrationProgressTable";
import { FilterLineIcon } from "../../../components/common/Icons/Icons";

// URL params the filter panel owns (counted on the Filters button)
const filterParams = ["spa_id", "stage", "errors_only"];

interface ContainerProps {
  onRefresh: () => void;
}

const MigrationProgressContainer: React.FC<ContainerProps> = ({ onRefresh }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, loading } = useSelector((state: RootState) => state.spaUpdateProgress);

  // Open by default: every filter in view
  const [showFilters, setShowFilters] = useState(true);

  // Pending filter state — only committed to the URL (and so to the fetch) on Apply.
  const [spaId, setSpaId] = useState("");
  const [stage, setStage] = useState("all");
  const [errorsOnly, setErrorsOnly] = useState(false);

  useEffect(() => {
    setSpaId(searchParams.get("spa_id") ?? "");
    setStage(searchParams.get("stage") ?? "all");
    setErrorsOnly(searchParams.get("errors_only") === "1");
  }, [searchParams]);

  const setOrDelete = (params: URLSearchParams, key: string, value: string) => {
    value === "" || value === "all" ? params.delete(key) : params.set(key, value);
  };

  const handleApply = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("page"); // reset page
    setOrDelete(next, "spa_id", spaId.trim());
    setOrDelete(next, "stage", stage);
    errorsOnly ? next.set("errors_only", "1") : next.delete("errors_only");
    setSearchParams(next);
  };

  const handleClear = () => {
    setSpaId(""); setStage("all"); setErrorsOnly(false);
    const next = new URLSearchParams(searchParams);
    ["page", "spa_id", "stage", "errors_only"].forEach((k) => next.delete(k));
    setSearchParams(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") handleApply(); };

  // Stages come from the server (SpaUpdateProgress::STAGES) so the list can't drift from
  // the write validation. Fold in the selected value if it isn't there yet — covers the
  // first render of a deep link like ?stage=failed, and any legacy value since the
  // column itself is an unconstrained string.
  const stageOptions: string[] = filters?.stages ?? [];
  const stageSelectOptions = stage !== "all" && !stageOptions.includes(stage)
    ? [...stageOptions, stage]
    : stageOptions;

  const activeFilters = filterParams.filter((param) => searchParams.has(param)).length;

  const toolbar = (
    <>
      <button
        type="button"
        className={`btn btn-sm dt-tool-btn lp-filter-toggle ${showFilters ? "active" : ""}`}
        onClick={() => setShowFilters(!showFilters)}
        aria-expanded={showFilters}
        aria-controls="lp-filters"
      >
        <FilterLineIcon/> Filters
        {activeFilters > 0 && <span className="lp-count">{activeFilters}</span>}
      </button>

      <AutoRefreshControl onRefresh={onRefresh} busy={loading} variant="toolbar" />
    </>
  );

  return (
    <div className={`lp-layout ${showFilters ? "" : "filters-hidden"}`}>
      {showFilters && (
        <aside className="lp-filters" id="lp-filters" aria-label="Filters">
          <div className="lp-filters-body">
            <div className="lp-group">
              {/* One GUID matches partially; a comma-separated list matches exactly —
                  same contract as the Guid filter on the Spas page. */}
              <TextField
                id="guid-search"
                label="GUID"
                type="search"
                value={spaId}
                onChange={(e) => setSpaId(e.target.value)}
                onKeyDown={handleKeyDown}
                size="small"
                fullWidth
                inputProps={{ maxLength: 1990 }}
              />

              <FormControl size="small" fullWidth>
                <InputLabel id="stage-filter-label">Stage</InputLabel>
                <Select
                  labelId="stage-filter-label"
                  id="stage-filter"
                  label="Stage"
                  value={stage}
                  onChange={(e) => setStage(String(e.target.value))}
                >
                  <MenuItem value="all">All</MenuItem>
                  {stageSelectOptions.map((option: string) => (
                    <MenuItem key={option} value={option} className="mp-stage-option">{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>

            <div className="lp-group">
              <div className="lp-toggles">
                <button type="button" className={`lp-toggle ${errorsOnly ? "on" : ""}`} aria-pressed={errorsOnly} onClick={() => setErrorsOnly(!errorsOnly)}>
                  <span className="lp-toggle-box" aria-hidden="true"/>
                  Errors only
                </button>
              </div>
            </div>
          </div>

          <div className="lp-filters-foot">
            <button type="button" className="btn btn-sm lp-clear-btn" onClick={handleClear}>Clear</button>
            <button type="button" className="btn btn-sm btn-primary" onClick={handleApply}>Apply Filters</button>
          </div>
        </aside>
      )}

      <MigrationProgressTable toolbar={toolbar}/>
    </div>
  );
};

export default MigrationProgressContainer;
